<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { fetchInlineAttachment, type Todo, type TodoAttachment } from '@/api'
import { gunzipJson, isOwnFileLink, mediaKind, type MediaKind } from '@/todos/media'

const props = defineProps<{ todo: Todo; calendarId: number }>()

interface Item {
  attachment: TodoAttachment
  kind: MediaKind
}

const { t } = useI18n()
const items = computed<Item[]>(() =>
  props.todo.attachments.flatMap((attachment) => {
    const kind = mediaKind(attachment)
    // A link we do not host is only listed, never loaded.
    const loadable = attachment.uri === null || isOwnFileLink(attachment.uri)
    return kind && loadable ? [{ attachment, kind }] : []
  }),
)

// Object URLs of inline attachments by index, and the ones that failed (they stay in the attachment list).
const blobUrls = ref<Record<number, string>>({})
const failed = ref<Record<number, true>>({})
const containers = new Map<number, HTMLElement>()
const animations = new Map<number, { destroy(): void }>()
let unmounted = false

function fail(index: number): void {
  failed.value = { ...failed.value, [index]: true }
}

async function bytesOf(attachment: TodoAttachment): Promise<{ blob: Blob }> {
  if (attachment.uri) {
    const response = await fetch(attachment.uri)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return { blob: await response.blob() }
  }
  return { blob: await fetchInlineAttachment(props.calendarId, props.todo.uid, attachment.index) }
}

async function load(item: Item): Promise<void> {
  const { attachment, kind } = item
  const index = attachment.index
  try {
    if (kind === 'tgs') {
      const { blob } = await bytesOf(attachment)
      const [{ default: lottie }, animationData] = await Promise.all([
        import('lottie-web/build/player/lottie_light'),
        gunzipJson(await blob.arrayBuffer()),
      ])
      const container = containers.get(index)
      if (unmounted || !container) return
      animations.get(index)?.destroy()
      animations.set(
        index,
        lottie.loadAnimation({ container, renderer: 'svg', loop: true, autoplay: true, animationData }),
      )
    } else if (attachment.uri === null) {
      const { blob } = await bytesOf(attachment)
      if (unmounted) return
      const typed = new Blob([blob], { type: attachment.fmttype ?? blob.type })
      blobUrls.value = { ...blobUrls.value, [index]: URL.createObjectURL(typed) }
    }
  } catch {
    fail(index)
  }
}

const started = new Set<string>()
watchEffect(() => {
  for (const item of items.value) {
    const key = `${item.attachment.index}:${item.attachment.uri ?? ''}:${item.kind}`
    if (started.has(key) || (item.kind !== 'tgs' && item.attachment.uri !== null)) continue
    started.add(key)
    // The tgs container only exists after the render, the blob ones need no element.
    void nextTick().then(() => load(item))
  }
})

function source(item: Item): string | undefined {
  return item.attachment.uri ?? blobUrls.value[item.attachment.index]
}

function bind(index: number, element: unknown): void {
  if (element instanceof HTMLElement) containers.set(index, element)
  else containers.delete(index)
}

onBeforeUnmount(() => {
  unmounted = true
  for (const animation of animations.values()) animation.destroy()
  for (const url of Object.values(blobUrls.value)) URL.revokeObjectURL(url)
})
</script>

<template>
  <div v-if="items.some((item) => !failed[item.attachment.index])" class="media">
    <template v-for="item in items" :key="item.attachment.index">
      <template v-if="!failed[item.attachment.index]">
        <img
          v-if="item.kind === 'image' && source(item)"
          :src="source(item)"
          :alt="item.attachment.filename ?? t('attachments.numbered', { n: item.attachment.index + 1 })"
          loading="lazy"
          @error="fail(item.attachment.index)"
        />
        <video
          v-else-if="item.kind === 'video' && source(item)"
          :src="source(item)"
          controls
          muted
          loop
          playsinline
          @error="fail(item.attachment.index)"
        />
        <div
          v-else-if="item.kind === 'tgs'"
          :ref="(element) => bind(item.attachment.index, element)"
          class="tgs"
          role="img"
          :aria-label="item.attachment.filename ?? t('attachments.numbered', { n: item.attachment.index + 1 })"
        />
      </template>
    </template>
  </div>
</template>

<style scoped>
.media {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

img,
video {
  max-width: 100%;
  max-height: 20rem;
  border-radius: 10px;
}

.tgs {
  width: 10rem;
  height: 10rem;
}
</style>
